import { getApi, noAuthGetApi, noAuthPostApi, noAuthPutApi } from './apis';

const requestCache = new Map();

const cacheKey = (endpoint: string, params?: any) => {
  return `${endpoint}${params ? JSON.stringify(params) : ''}`;
};

const withCache = async (key: string, request: () => Promise<any>) => {
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = request();
  requestCache.set(key, promise);

  try {
    const result = await promise;
    setTimeout(() => requestCache.delete(key), 2000);
    return result;
  } catch (error) {
    requestCache.delete(key);
    throw error;
  }
};

export const fetchApplicationDropdownData = async (type: string) => {
  let endpoint = "";

  switch (type) {
    case "calYear":
      endpoint = "ProductCostApplication/GetCalendarYear";
      break;
    case "activity":
      endpoint = "ProductCostApplication/GetActivity";
      break;
    case "productType":
      endpoint = "ProductCostApplication/GetProductType";
      break;
    default:
      throw new Error("Invalid dropdown type");
  }

  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data for ${type}: ${error.message}`);
      }
      throw new Error(`Failed to fetch data for ${type}: unknown error occurred`);
    }
  });
};

export const fetchApplicationProductName = async (value: string, calyear: number) => {
  const endpoint = `ProductCostApplication/GetProductsNamesBasedOnType?Type=${value}&CalYear=${calyear}`;
  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error('Failed to fetch data: unknown error occurred');
    }
  });
};

export const fetchSeedsUnitsDropdown = async (type: any) => {
  const endpoint = `SeedCost/units`;
  try {
    return await getApi(endpoint, type);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to load Seeds cost units in Ui");
  }
}

export const getCropsData = async(data: any) => {
  const endpoint = `CropType/dropdown`;
  try {
    return await getApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to fetch data: ${error.message}`);
    }
    throw new Error("Failed to load Seeds cost units in Ui");
  }
}

export const fetchApplicationDropdown = async (type: string, value: number): Promise<any> => {
  let endpoint = "";

  switch (type) {
    case "genericName":
      endpoint = `ProductCostApplication/GetGenericName?Id=${value}`;
      break;
    case "price":
      endpoint = `ProductCostApplication/GetPrice?Id=${value}`;
      break;
    case "quantityUnits":
      endpoint = `ProductCostApplication/GetQuantityUnits?Id=${value}`;
      break;
    case "priceUnits":
      endpoint = `ProductCostApplication/GetPriceUnits?Id=${value}`;
      break;
    case "rateUnits":
      endpoint = `ProductCostApplication/GetRateUnits?Id=${value}`;
      break;
    default:
      throw new Error("Invalid dropdown type");
  }

  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error('Failed to fetch data: unknown error occurred');
    }
  });
};

export const fetchApplicationGrid = async (value: number) => {
  const endpoint = `ProductCostApplication/GetAllProductCostApplicationGridViewData?userId=${value}`;
  
  return withCache(cacheKey(endpoint), async () => {
    try {
      return await noAuthGetApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data: ${error.message}`);
      }
      throw new Error('Failed to fetch data: unknown error occurred');
    }
  });
};

export const postApplicationData = async (data: any) => {
  try {
    return await noAuthPostApi('ProductCostApplication/AddProductCostApplicationGridView', data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to post data: ${error.message}`);
    }
    throw new Error('Failed to post data: unknown error occurred');
  }
};

export const approveApplicationGridView = async (data: any) => {
  try {
    return await noAuthPostApi('ProductCostApplication/ApproveProductCostApplicationData', data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to update data: ${error.message}`);
    }
    throw new Error('Failed to update data: unknown error occurred');
  }
};

export const updateApplicationGridView = async (data: any) => {
  try {
    return await noAuthPutApi('ProductCostApplication/EditGridViewData', data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to update data: ${error.message}`);
    }
    throw new Error('Failed to update data: unknown error occurred');
  }
};

export const deleteApplicationGridView = async (data: any) => {
  try {
    return await noAuthPutApi(`ProductCostApplication/DeleteGridViewData?Id=${data}`);
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(`Failed to delete data: ${error.message}`);
    }
    throw new Error('Failed to delete data: unknown error occurred');
  }
};